import { motion } from "framer-motion";
import { Sparkles, PartyPopper, UtensilsCrossed } from "lucide-react";

const BALLOON_IMG =
  "https://images.unsplash.com/photo-1746309135204-25b148b4375a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHw0fHxwYXN0ZWwlMjBiYWxsb29uJTIwZ2FybGFuZCUyMGx1eHVyeXxlbnwwfHx8fDE3ODM1NTY1MTN8MA&ixlib=rb-4.1.0&q=85";
const DECOR_IMG =
  "https://images.unsplash.com/photo-1602468690798-0820b3ec6ee7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwyfHxyb21hbnRpYyUyMGJsdXNoJTIwZXZlbnQlMjBkZWNvciUyMHRhYmxlc2NhcGV8ZW58MHx8fHwxNzkxNDY3ODQxfDA&ixlib=rb-4.1.0&q=85";

const SERVICES = [
  {
    icon: Sparkles,
    title: "Event Styling & Decor",
    copy: "Full tablescapes, florals, candlelight and linens — every surface considered, every detail in its place.",
  },
  {
    icon: PartyPopper,
    title: "Balloon Garlands",
    copy: "Cascading organic garlands and arches in soft, curated palettes that frame your moments beautifully.",
  },
  {
    icon: UtensilsCrossed,
    title: "Private Chef Catering",
    copy: "Our in-house chef designs bespoke menus across Caribbean, Asian and American cuisines — plated to perfection.",
  },
];

const reveal = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
};

export default function Services() {
  return (
    <section id="services" data-testid="services-section" className="py-28 lg:py-36">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-32">
            <motion.p {...reveal} transition={{ duration: 0.7 }} className="eyebrow">
              What We Do
            </motion.p>
            <motion.h2
              {...reveal}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="mt-6 font-serif font-light text-4xl sm:text-5xl leading-tight text-[#2A2423]"
            >
              Styled, set &amp; <em className="text-[#D8A7A1]">served</em> — all under one roof
            </motion.h2>
            <motion.p
              {...reveal}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="mt-8 text-base leading-relaxed text-muted-foreground max-w-md"
            >
              From the first balloon to the final course, The Diamond Effect Affairs designs the atmosphere and the menu
              together — so your celebration feels seamless, romantic and entirely yours.
            </motion.p>
            <motion.div {...reveal} transition={{ duration: 0.8, delay: 0.3 }} className="mt-12 space-y-8">
              {SERVICES.map((s, i) => (
                <div key={s.title} data-testid={`service-item-${i}`} className="flex gap-5 group">
                  <div className="shrink-0 w-11 h-11 rounded-full border border-[#E5D3B3] flex items-center justify-center text-[#D8A7A1] transition-colors duration-300 group-hover:bg-[#D8A7A1] group-hover:text-[#FDFBF7]">
                    <s.icon className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="font-serif text-xl text-[#2A2423]">{s.title}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{s.copy}</p>
                  </div>
                </div>
              ))}
            </motion.div>
          </div>
        </div>

        <div className="lg:col-span-7 flex flex-col gap-10">
          <motion.div {...reveal} transition={{ duration: 0.9 }} className="group">
            <div className="overflow-hidden border border-[#E5D3B3] aspect-[16/10]">
              <img
                src={BALLOON_IMG}
                alt="Pastel balloon garland arch"
                data-testid="services-balloon-image"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
            </div>
            <p className="mt-4 text-xs uppercase tracking-[0.22em] text-muted-foreground">Signature balloon garlands</p>
          </motion.div>
          <motion.div {...reveal} transition={{ duration: 0.9, delay: 0.1 }} className="group lg:ml-24">
            <div className="overflow-hidden rounded-t-full border border-[#E5D3B3] aspect-[4/5] max-w-md">
              <img
                src={DECOR_IMG}
                alt="Elegant tablescape with white florals"
                data-testid="services-decor-image"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
            </div>
            <p className="mt-4 text-xs uppercase tracking-[0.22em] text-muted-foreground">Romantic table styling</p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
